import React from "react";
import { PaperClipIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { PersonDocument } from "../../api/360Files";
import { classNames, copyToClipboard } from "../../utils";
import CopyOutlineIcon from "../../shared-components/icons/CopyOutlineIcon";

interface PersonInfoFieldProps {
  label: string;
  value?: string;
  values?: string[];
  docs?: PersonDocument[];
  isUrl?: boolean;
  /** When true, values preserve newlines (e.g. Employment, Education). */
  allowMultiline?: boolean;
  onCopy?: () => void;
  showEditIcon?: boolean;
  onEditClick?: () => void;
  /**
   * When set, the row is always rendered; if there is no data, this text is shown instead of hiding the section.
   * Use "None" or "Not Provided" per product copy for empty Person Info on 360 workbench.
   */
  emptyPlaceholder?: string;
}

const PersonInfoField: React.FC<PersonInfoFieldProps> = ({
  label,
  value,
  values,
  docs,
  isUrl = false,
  allowMultiline = false,
  onCopy,
  showEditIcon = false,
  onEditClick,
  emptyPlaceholder,
}) => {
  const handleCopy = async (text: string) => {
    await copyToClipboard(text);
    onCopy?.();
  };

  const hasValue = !!(
    (value && value.trim()) ||
    (values && values.filter(Boolean).length > 0) ||
    (docs && docs.length > 0)
  );

  if (!hasValue && emptyPlaceholder === undefined) {
    return null;
  }

  const displayValue = hasValue ? value : undefined;
  const displayValues = hasValue ? values : undefined;
  const displayDocs = hasValue ? docs : undefined;

  return (
    <div className="mb-3 text-sm w-full">
      <div className="flex items-center gap-1.5 mb-0.5">
        <span className="text-sm text-gray-500">{label}</span>
        {showEditIcon && (
          <button
            type="button"
            onClick={onEditClick}
            className="p-0.5 -m-0.5 cursor-pointer text-gray-700 hover:text-gray-400 flex-shrink-0"
            title={`Edit ${label}`}
            aria-label={`Edit ${label}`}
          >
            <PencilSquareIcon className="w-4 h-4" aria-hidden="true" />
          </button>
        )}
      </div>
      {!hasValue && emptyPlaceholder !== undefined && (
        <span className="block text-sm leading-5 text-gray-500">
          {emptyPlaceholder}
        </span>
      )}
      {hasValue && displayValue !== undefined && (
        <div className="flex justify-between items-center gap-2">
          <span className="block font-semibold text-sm leading-5 text-gray-700 truncate">
            {displayValue}
          </span>
          <button
            type="button"
            className="p-0.5 cursor-pointer text-gray-500 hover:text-gray-700 flex-shrink-0"
            onClick={() => handleCopy(displayValue)}
            title="Copy to clipboard"
          >
            <CopyOutlineIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {displayValues &&
        displayValues.map((val, index) => (
          <div
            key={index}
            className={classNames(
              "flex justify-between items-center gap-2",
              allowMultiline ? (index > 0 ? "mt-2" : "") : "mt-0.5"
            )}
          >
            {isUrl ? (
              <span
                className="block  font-semibold text-sm leading-5 text-gray-700 hover:underline truncate cursor-pointer"
                onClick={() => window.open(val, "_blank")}
                title={val}
              >
                {val}
              </span>
            ) : (
              <span
                className={classNames(
                  "block text-sm  font-semibold leading-5 text-gray-700",
                  allowMultiline ? "whitespace-pre-line" : "truncate"
                )}
              >
                {val}
              </span>
            )}
            <button
              type="button"
              className="p-0.5 cursor-pointer text-gray-500 hover:text-gray-700 flex-shrink-0"
              onClick={() => handleCopy(val)}
              title="Copy to clipboard"
            >
              <CopyOutlineIcon className="w-4 h-4" />
            </button>
          </div>
        ))}
      {displayDocs &&
        displayDocs.length > 0 &&
        displayDocs.map((doc) => (
          <div
            key={doc.url}
            className="flex gap-2 items-center cursor-pointer mt-0.5 group"
            onClick={() => window.open(doc.url, "_blank")}
          >
            <PaperClipIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <span className="truncate flex-1 text-sm font-semibold text-gray-700">
              {doc.filename}
            </span>
            <button
              type="button"
              className="p-0.5 cursor-pointer text-gray-500 hover:text-gray-700 flex-shrink-0 opacity-0 group-hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                handleCopy(doc.url);
              }}
              title="Copy link"
            >
              <CopyOutlineIcon className="w-4 h-4" />
            </button>
          </div>
        ))}
    </div>
  );
};

export default PersonInfoField;
